import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode as decode } from "jwt-decode"
import { refreshAuthToken } from '../utils/auth';
import {
  persistTokens,
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
  updateRefreshToken,
  clearTokens,
} from '../utils/tokenStorage';
import { setAuthFailureHandler } from '../utils/apiClient';
import { logWarn } from '../observability/logger';
import { isCognitoMode } from '../utils/authMode';
import { getCognitoSessionTokens, logoutCognito } from '../utils/cognitoAuth';
import { extractPrincipalFromToken } from '../utils/authPrincipal';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(null);
  const [principal, setPrincipal] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const navigate = useNavigate();

  const setAuthenticatedToken = useCallback((accessToken) => {
    setAuthToken(accessToken);
    setPrincipal(extractPrincipalFromToken(accessToken));
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      if (isCognitoMode()) {
        try {
          const { accessToken } = await getCognitoSessionTokens();
          if (accessToken) {
            updateAccessToken(accessToken);
            setAuthenticatedToken(accessToken);
          } else {
            clearTokens();
            setAuthenticatedToken(null);
          }
        } catch {
          clearTokens();
          setAuthenticatedToken(null);
        } finally {
          setIsInitializing(false);
        }
        return;
      }

      const token = getAccessToken();
      if (!token) {
        setAuthenticatedToken(null);
        setIsInitializing(false);
        return;
      }

      try {
        const decoded = decode(token);
        if (decoded.exp * 1000 > Date.now()) {
          setAuthenticatedToken(token);
          setIsInitializing(false);
          return;
        }

        const currentRefreshToken = getRefreshToken();
        const refreshed = await refreshAuthToken(currentRefreshToken);
        updateAccessToken(refreshed.access_token);
        updateRefreshToken(refreshed.refresh_token);
        setAuthenticatedToken(refreshed.access_token);
      } catch {
        clearTokens();
        setAuthenticatedToken(null);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [setAuthenticatedToken]);

  const handleLogin = ({ accessToken, refreshToken, remember }) => {
    try {
      persistTokens({ accessToken, refreshToken: refreshToken || '', remember });
      setAuthenticatedToken(accessToken);
      navigate('/main');
    } catch (error) {
      console.error('Erro ao fazer login:', error);
    }
  };

  const logout = useCallback(() => {
    try {
      if (isCognitoMode()) {
        void logoutCognito();
      }
      setAuthenticatedToken(null);
      clearTokens();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  }, [navigate, setAuthenticatedToken]);

  useEffect(() => {
    setAuthFailureHandler(() => {
      logWarn('auth.session.expired');
      logout();
    });

    return () => {
      setAuthFailureHandler(null);
    };
  }, [logout]);

  return (
    <AuthContext.Provider value={{ authToken, principal, isInitializing, handleLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
